export const DEMO_PREVIEW_PLACEMENT = { seller: "5014", subseller: "3475" };

export const WIDGET_OPTION_SECTIONS = [
  {
    key: "widget",
    label: "Widget",
    advanced: false,
    options: [
      {
        group: null,
        key: "theme",
        label: "Theme",
        type: "enum",
        values: [
          { label: "Light", value: "light" },
          { label: "Dark", value: "dark" },
        ],
        default: "light",
        tooltip: "Surface of the card. A tag attribute still wins over this.",
        attribute: (value) => `theme="${value}"`,
      },
      {
        group: null,
        key: "show_progress_bar",
        label: "Progress bar",
        type: "boolean",
        default: true,
        tooltip: "The bar under the card showing playback position.",
        attribute: (value) => (value ? null : "hide-progress-bar"),
      },
      {
        group: null,
        key: "show_backdrop",
        label: "Backdrop",
        type: "boolean",
        default: true,
        tooltip:
          "The surface the card sits on. Off, only the shell and its buttons show.",
        attribute: (value) => (value ? null : "hide-backdrop"),
      },
      {
        group: null,
        key: "grain",
        label: "Grain",
        type: "boolean",
        default: true,
        tooltip: "Texture overlay on the card.",
        attribute: (value) => `grain="${value}"`,
      },
    ],
  },
  {
    key: "colors",
    label: "Colors",
    advanced: false,
    inline: true,
    options: [
      {
        group: "colors",
        key: "gradient_color",
        label: "Gradient",
        type: "color",
        default: "",
        tooltip: "Empty means the color comes from the cover image.",
        attribute: (value) => `gradient-color="${value}"`,
      },
      {
        group: "colors",
        key: "primary_button_color",
        label: "Button",
        type: "color",
        default: "",
        tooltip: "Empty means the color comes from the cover image.",
        attribute: (value) => `primary-button-color="${value}"`,
      },
      {
        group: "colors",
        key: "active_word_color",
        label: "Active word",
        type: "color",
        default: "",
        tooltip: "Empty falls back to the gradient color.",
        attribute: (value) => `active-word-color="${value}"`,
      },
      {
        group: "colors",
        key: "active_word_color_dark",
        label: "Active word (dark)",
        type: "color",
        default: "",
        tooltip:
          "Used only on the dark theme. Empty keeps the one picked from the cover image, which is chosen to stay readable on black.",
        attribute: (value) => `active-word-color-dark="${value}"`,
      },
    ],
  },
  {
    key: "fab",
    label: "Floating player",
    advanced: false,
    options: [
      {
        group: "fab",
        key: "hidden",
        label: "Show player",
        type: "boolean",
        default: false,
        inverted: true,
        tooltip: "The small player that follows the visitor down the page.",
        attribute: (value) => (value ? "hide-fab" : null),
      },
      {
        group: "fab",
        key: "placement",
        label: "Side",
        type: "enum",
        values: [
          { label: "Left", value: "left" },
          { label: "Right", value: "right" },
        ],
        default: "right",
        tooltip: "Which side of the viewport it sits on.",
        attribute: (value) => `fab-placement="${value}"`,
      },
      {
        group: "fab",
        key: "intersection",
        label: "Hide when card visible",
        type: "boolean",
        default: true,
        tooltip: "Steps aside while the card itself is on screen.",
        attribute: (value) => (value ? null : 'fab-intersection="false"'),
      },
    ],
  },
  {
    key: "advanced",
    label: "Advanced",
    advanced: true,
    options: [
      {
        group: null,
        key: "resume_from_preview",
        label: "Start from preview position",
        type: "boolean",
        default: false,
        tooltip: "Continue where the silent preview reached. Needs preload.",
        attribute: (value) => `resume-from-preview="${value}"`,
      },
      {
        group: null,
        key: "preload",
        label: "Preload audio",
        type: "boolean",
        default: false,
        tooltip: "Fetch audio before the visitor turns sound on.",
        attribute: (value) => `preload="${value}"`,
      },
      {
        group: null,
        key: "cache",
        label: "Keep audio cached",
        type: "boolean",
        default: true,
        tooltip: "Replaying a snippet costs no bytes.",
        attribute: (value) => `cache="${value}"`,
      },
    ],
  },
];

const allOptions = () =>
  WIDGET_OPTION_SECTIONS.flatMap((section) => section.options);

// Every option lives under a single `widget` block; `group` is the sub-object
// inside it, or null for a key sitting directly on `widget`.
const readStored = (document, option) =>
  option.group
    ? document?.widget?.[option.group]?.[option.key]
    : document?.widget?.[option.key];

export const buildFormModel = (stored) => {
  const document = typeof stored === "string" ? JSON.parse(stored) : stored;
  const model = {};
  for (const option of allOptions()) {
    const bucket = option.group || "widget";
    model[bucket] = model[bucket] || {};
    const saved = readStored(document, option);
    model[bucket][option.key] = saved === undefined ? option.default : saved;
  }
  return model;
};

export const toDocument = (model, carriedOver = null) => {
  const widget = carriedOver?.widget ? { ...carriedOver.widget } : {};
  for (const option of allOptions()) {
    const value = model?.[option.group || "widget"]?.[option.key];
    if (value === undefined || value === null || value === "") continue;
    if (value === option.default) continue;
    const clean = typeof value === "string" ? value.trim() : value;
    if (option.group) {
      widget[option.group] = widget[option.group] || {};
      widget[option.group][option.key] = clean;
    } else {
      widget[option.key] = clean;
    }
  }
  return Object.keys(widget).length ? { widget } : null;
};

export const toPreviewAttributes = (model) => {
  const attributes = [];
  for (const option of allOptions()) {
    const value = model?.[option.group || "widget"]?.[option.key];
    if (value === undefined || value === null || value === "") continue;
    const attribute = option.attribute(value);
    if (attribute) attributes.push(attribute);
  }
  return attributes;
};

export const toInlineAttributes = (model) => {
  const attributes = [];
  for (const option of allOptions()) {
    const value = model?.[option.group || "widget"]?.[option.key];
    if (value === undefined || value === null || value === "") continue;
    if (value === option.default) continue;
    const attribute = option.attribute(value);
    if (attribute) attributes.push(attribute);
  }
  return attributes;
};
